/**
 * Profile Service Types
 *
 * Centralized type definitions for all profile-related data structures
 * Used across server actions, API calls, and UI components
 */

import { z } from 'zod';
import type { ThemeChoice } from '@/lib/utils/theme';

// ============================================================================
// Core Profile Types
// ============================================================================

/**
 * User Profile - Represents an individual user's profile
 */
/**
 * What a formal letter signed by this user prints below the signature image.
 */
export interface EffectiveLetterFooter {
  /** The rendered block. `''` only when the firm prints no signer name at all. */
  html: string;
  /**
   * Which block won. `default` means the firm's standard name-and-title sign-off
   * — a real sign-off, not the absence of one.
   */
  source: 'personal' | 'default' | 'none';
}

/**
 * The signature a user's outbound email actually carries, as the backend
 * resolved it (VALE-445).
 */
export interface EffectiveEmailSignature {
  /** The rendered signature, merge fields expanded. `''` when the firm has none. */
  html: string;
  /**
   * The **firm-wide** signature, merge fields expanded for this same user —
   * reported whether or not it is the one that won in `html`, and whether or not
   * the firm has signatures switched off.
   *
   * This is what the branding card's read state renders, and the reason it
   * exists: the card edits the firm signature, so on a firm that permits
   * personal overrides `html` is the viewing admin's *own* signature and is the
   * wrong thing to show. The card used to fall back to the raw stored firm HTML
   * in that case, and `sanitizeHtml` (DOMPurify) deletes an unexpanded
   * `<<token>>` outright — printing `<>` on every merge-field line.
   *
   * Expanded server-side, like `html`: the resolvers are the backend's and a
   * frontend copy would be a second definition of what the recipient sees.
   */
  firmHtml: string;
  /**
   * The firm-wide signature **as authored, merge fields NOT expanded** — what
   * the editor opens on when a user with no personal signature of their own
   * starts writing one (VALE-1242).
   *
   * Never render this: the sanitiser deletes an unexpanded `<<token>>` outright,
   * which is what `firmHtml` above is for. Seeding the editor with the expanded
   * form instead would bake this viewer's own name and contact details in as
   * literal text, so their signature would go stale the day any of them changes.
   */
  firmStoredHtml: string;
  /** Which signature won. */
  source: 'personal' | 'firm' | 'none';
  /**
   * Whether this firm permits a personal signature at all. When `false`, the
   * profile card shows the firm signature read-only in place of the editor, so
   * the user can still see what they send and understand why they cannot change
   * it. **Default on** (VALE-1242).
   */
  overrideAllowed: boolean;
  /**
   * Whether the firm has Vale's HTML email signature switched on at all
   * (VALE-490). When `false` nothing is appended to any outbound email —
   * personal signatures included — and every surface promising one hides.
   *
   * Reported separately because it cannot be inferred from `source: 'none'`,
   * which also means "no signature has been configured".
   */
  enabled: boolean;
  /**
   * Signature-authorable tokens the effective signature uses that *this* user
   * cannot resolve. Advisory — a missing value never blocks a send.
   */
  unresolvedTokens: string[];
}

export interface UserProfile {
  id: number;
  email: string;
  /**
   * Nullable (LF-6015). `profile.service.ts` returns `lawFirmUser.firstName` /
   * `.lastName` with no coalesce, straight off the nullable columns, and
   * `UpdateProfileDto` marks both `@IsOptional()` — so nothing in the contract
   * guarantees a name. (Every *currently* active user happens to have one, but
   * that is data, not a constraint.) This service's own `getUserDisplayName` /
   * `getUserInitials` in `./utils.ts` were already written defensively for
   * null; the type now agrees with them.
   */
  firstName: string | null;
  middleName?: string | null;
  lastName: string | null;
  phoneNumber?: string;
  /**
   * Job title / position, as one free-text line including any post-nominals —
   * "Senior Associate, LL.B". Rendered by the `<<lawyer.title>>` merge field and
   * beneath the signer name on formal letters.
   *
   * Backed by a real `law_firm_users.title` column as of VALE-445. It was
   * previously declared under "extended fields (may not be in basic response)"
   * with nothing behind it at all.
   *
   * **Not `role`** — that is the internal Vale permission
   * (`admin` / `user` / `billing_only`) and must never reach a third party.
   */
  title?: string | null;
  /** Their profile page on the firm's website. Null when not recorded. */
  profileUrl?: string | null;
  /**
   * Pronouns, as the pronoun string itself and unbracketed — `he/him`
   * (VALE-1227). Null when not recorded, which is the default.
   *
   * Rendered by the `<<signatory.pronouns>>` email-signature merge field, which
   * supplies its own brackets, so a user who has recorded none renders nothing
   * in the token's place rather than an empty `()`.
   */
  pronouns?: string | null;
  /**
   * The office this user works out of (VALE-540), or null when not recorded.
   *
   * Feeds the `office.*` email-signature merge fields. It does **not** select a
   * letter's letterhead — that follows the matter's office, never the signer's.
   */
  officeId?: number | null;
  // Personal HTML email signature. Overrides the firm-wide signature on
  // outbound email this user sends/signs; null/undefined → firm signature.
  htmlEmailSignature?: string | null;
  htmlLetterFooter?: string | null;
  /**
   * What this user's outbound email is **actually** signed with — resolved and
   * merge-field-expanded server-side (VALE-445).
   *
   * The signature card's read state renders `html` directly. Deliberately not
   * derived here: choosing between the personal and the firm signature depends
   * on a firm setting, and expanding the tokens depends on the backend's
   * merge-field resolvers — so a frontend copy would be a second definition of
   * what the recipient sees, free to disagree with the send path.
   */
  effectiveEmailSignature?: EffectiveEmailSignature;
  /**
   * What a formal letter signed by this user prints **below the signature** —
   * their own footer when they have written one, otherwise the firm's default
   * name-and-title sign-off.
   *
   * The `default` case is the common one and is **not** an empty state: it is a
   * working sign-off the user's letters already carry. Resolved server-side for
   * the same reason as `effectiveEmailSignature` — the default block's markup and
   * the firm toggles that gate it belong to the letter generator, and a document
   * a client receives must not have two definitions.
   */
  effectiveLetterFooter?: EffectiveLetterFooter;
  role?: string;
  userType?: 'regular' | 'admin';
  isActive?: boolean;
  isStaff?: boolean;
  /**
   * When the user finished or dismissed the first-sign-in onboarding flow, or null
   * when they never did (VALE-1788).
   *
   * Sent to Intercom as the boolean `onboarding_completed`, so a tour or checklist
   * can be targeted at the users still to be walked through the product. The
   * timestamp itself is deliberately not sent: nothing segments on *when*.
   */
  onboardingCompletedAt?: string | null;
  lawFirm?: {
    id: number;
    name: string;
    faviconUrl?: string;
    websiteUrl?: string;
    /**
     * When the firm's Vale account was created. Intercom company `created_at`
     * (VALE-1788).
     */
    createdAt?: string;
    /** The firm's plan. Intercom company `plan` (VALE-1788). */
    membershipTier?: 'standard' | 'professional' | 'enterprise';
    /**
     * Where the firm sits in its membership lifecycle (VALE-1788). Carried
     * alongside the tier rather than folded into it: "trialling on professional"
     * and "active on standard" are different segments.
     */
    membershipStatus?:
      | 'pending'
      | 'trial'
      | 'active'
      | 'active-free'
      | 'suspended'
      | 'cancelled';
    /**
     * Whether this is a Vale test tenant rather than a real firm (VALE-1788).
     * Sent so Intercom segments can exclude them, mirroring the
     * `is_test_account = false` filter every production aggregate applies.
     */
    isTestAccount?: boolean;
    /**
     * Whether staff have marked the firm's email signature setup complete
     * (VALE-1511). A status set by hand, never derived from whether the asset
     * exists.
     */
    emailSignatureSetupComplete?: boolean;
    /** Whether staff have marked the firm's letterhead setup complete (VALE-1511). */
    letterheadSetupComplete?: boolean;
    /**
     * Whether anyone at the firm has ever notified an institution (VALE-1884): the
     * Getting Started `notify-institutions` step, recorded for any of the firm's
     * users. Intercom company `has_notified_institution`, the audience rule for the
     * new-firm checklist.
     */
    hasNotifiedInstitution?: boolean;
    htmlEmailSignature?: string | null;
    distributionSettings?: {
      includeBankDetailsInAdvice: boolean;
      declarationsRequired?: boolean;
      sendModalDefaults?: {
        includeBankVerification: boolean;
        includeDeclaration: boolean;
      };
    };
  };
  // Extended fields (may not be in basic response)
  fullName?: string;
  emailSignature?: string;
  signatureFilename?: string;
  avatar?: string;
  preferences?: {
    /**
     * The user's persisted light/dark/system CHOICE (VALE-548).
     *
     * Read-only from the frontend's point of view at first paint — the theme is
     * PAINTED from a cookie the server reads before any JavaScript runs
     * (VALE-520), because this very endpoint is what the `dark-theme` flag
     * arrives on and it is async. This value seeds a browser that has no cookie
     * yet, and is written back only on an explicit toggle use.
     *
     * `system` here does not mean the user picked `system` — it is also what an
     * unwritten preference reads back as (the backend's documented default).
     */
    theme?: ThemeChoice;
    notifications?: { emailNotificationsEnabled?: boolean };
    correspondence?: { forwardCorrespondenceReplies?: boolean };
    matterViews?: {
      all?: MatterViewPreferences;
      archive?: MatterViewPreferences;
    };
    dismissedHelpBanners?: DismissedHelpBanners;
    gettingStarted?: GettingStartedState;
    /** Choices made in the correspondence-setup flow (VALE-1633). */
    onboarding?: { doesNotSignLetters?: boolean };
    /** @see FeatureIntroductions */
    featureIntroductions?: FeatureIntroductions;
  };
  status?: 'active' | 'invited' | 'inactive' | 'pending' | 'deactivated';
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  handwrittenSignatureFile?: {
    id: number;
    originalFilename: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    s3Key: string;
    imageS3Key?: string | null;
    mimeType: string;
    fileType: string;
    extractedData?: Record<string, unknown>;
    isCertifiedCopy: boolean;
    metadata?: Record<string, unknown>;
    analysisMetadata?: Record<string, unknown>;
    fileSizeKb?: number | null;
    deletedAt?: string | null;
  };
  totpEnabledAt?: string | null;
}

/**
 * Law Firm Profile - Represents the law firm/organization profile
 */
export interface LawFirmProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  website?: string;
  abn?: string; // Australian Business Number
  logo?: string;
  primaryContact?: ContactInfo;
  postalAddress?: Address;
  businessAddress?: Address;
  correspondencePreferences?: CorrespondencePreferences;
  trustBankAccount?: BankAccount;
  subscription?: SubscriptionInfo;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Banner for displaying important messages to users
 */
export interface Banner {
  /**
   * The banner row's numeric id. This is what `POST /profile/banners/:bannerId/dismiss`
   * is keyed on, so dismissal is impossible without it - which is exactly why no banner
   * could be dismissed before VALE-1787: the payload carried only the banner's name.
   */
  id: number;
  severity: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  linkUrl?: string;
  linkText?: string;
  /**
   * Whether this banner offers a close control. Spelled as the backend emits it on the
   * wire (`BannerDto.dismissable`); the entity and service side spell the column
   * `dismissible`. Optional because a frontend deploy can lead the backend that adds it -
   * absent means "not dismissible", which leaves the banner exactly as it renders today.
   */
  dismissable?: boolean;
}

/**
 * Profile Response - Response from GET /profile endpoint
 */
export interface ProfileResponse {
  user: UserProfile;
  permissions: string[];
  features: string[];
  banners?: Banner[];
  /**
   * True when this request arrived on a Vale staff impersonation session (VALE-1231).
   *
   * Resolved server-side from the impersonation register, so it cannot be forged or
   * suppressed by the client, and it dies with the Keycloak session. Optional because a
   * frontend deploy can lead the backend that adds it; absent means "not impersonated",
   * which is the safe default — it leaves every gate exactly as it is today.
   */
  impersonated?: boolean;
}

/**
 * Combined Profile Response - Used when fetching both user and firm data
 */
export interface ProfileData {
  user: UserProfile;
  lawFirm: LawFirmProfile;
}

// ============================================================================
// Supporting Types
// ============================================================================

export interface GettingStartedState {
  dismissed?: boolean;
}

/**
 * This user's history with one newly introduced surface (VALE-1996). Mirrors the
 * backend's `FeatureIntroduction` on `LawFirmUser.entity.ts`: ISO instants of the
 * user's own action, absent until it happens. `firstOpenedAt` retires the
 * surface's "New" marker; `feedbackDismissedAt` retires its feedback invitation.
 */
export interface FeatureIntroduction {
  firstOpenedAt?: string;
  feedbackDismissedAt?: string;
}

/** Keyed by `FeatureIntroductionKey` (`components/feature-introductions`). */
export type FeatureIntroductions = Partial<Record<string, FeatureIntroduction>>;

/**
 * The dismissible in-app help banners, one boolean per banner.
 *
 * **Five of the six keys ARE `StageType`** (`components/shared/stage-help/
 * stage-help-constants.ts`), which is the single prop `StageHelpBanner` takes —
 * so a stage-help key that renders nowhere is no longer expressible, and a new
 * banner is one entry in `STAGE_CONTENT` rather than three edits that can
 * disagree. `outlookConnect` is the sixth and the one exception: a setup nudge
 * with its own component, declared as such by the registry assertions.
 *
 * **The set mirrors the backend's `DismissedHelpBanners`**
 * (`backend/src/resources/entities/LawFirmUser.entity.ts`) and the mirror is
 * enforced from both directions, by `__tests__/help-banner-key-registry.test.ts`
 * here and its counterpart beside the backend entity. Two guards, not one,
 * because each package's PR gate short-circuits to green on a PR touching only
 * the other package (the same reason `backend/src/files/constants/__tests__/
 * frontend-accept-contract.spec.ts` exists).
 *
 * VALE-1789 reconciled this. LF-3166 renamed `verification` to `beneficiaries`
 * here and never touched the backend; `intake` and `declarations` named copy
 * that rendered on no page. It went unnoticed for four months because the stored
 * value is JSON and deep-merges, so neither half of the drift throws.
 */
export interface DismissedHelpBanners {
  discovery?: boolean;
  redemption?: boolean;
  /** Verification and declaration signing, one page since LF-3166. */
  beneficiaries?: boolean;
  payments?: boolean;
  templates?: boolean;
  /**
   * The in-app prompt asking a lawyer to connect their own Outlook mailbox
   * (LF-5666). A setup nudge rather than a stage help banner — it also stops
   * showing on its own once the user has a connected mailbox, and it is the one
   * key here with no `StageType` counterpart.
   */
  outlookConnect?: boolean;
}

export interface MatterViewPreferences {
  tags?: string[];
  types?: string[];
  teamMembers?: string[];
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  sortOrder?: 'ASC' | 'DESC';
}

export interface ContactInfo {
  fullName: string;
  email: string;
  phone: string;
  title?: string;
  department?: string;
}

export interface Address {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

export interface CorrespondencePreferences {
  replyToOption: 'user' | 'firm' | 'custom';
  customReplyEmail?: string;
  showUserContact: boolean;
  emailFooter?: string;
  autoCC?: string[];
}

export interface BankAccount {
  accountName: string;
  accountNumber: string;
  bsb: string;
  bankName: string;
  branch?: string;
}

export interface SubscriptionInfo {
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd?: string;
  trialEnd?: string;
  seats?: number;
  features?: string[];
}

// ============================================================================
// Request/Response Types for API Operations
// ============================================================================

export interface UpdateUserProfileRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phoneNumber?: string;
  /** Job title / position. Empty string clears it. */
  title?: string;
  /** Pronouns, unbracketed. Empty string clears it. */
  pronouns?: string;
  emailSignature?: string;
  signatureFilename?: string;
  // Personal HTML email signature. Empty string clears it (falls back to firm).
  htmlEmailSignature?: string;
  // Personal HTML letter footer. Empty string clears it (letters fall back to
  // rendering the signatory name only).
  htmlLetterFooter?: string;
}

export interface UpdateLawFirmProfileRequest {
  name?: string;
  email?: string;
  phone?: string;
  website?: string;
  primaryContact?: ContactInfo;
  postalAddress?: Address;
  businessAddress?: Address;
  correspondencePreferences?: CorrespondencePreferences;
  trustBankAccount?: BankAccount;
}

/**
 * Submit Suggestion Request - For submitting product feedback
 */
export interface SubmitSuggestionRequest {
  suggestion: string;
  fileIds: number[];
}

/**
 * Submit Suggestion Response
 */
export interface SubmitSuggestionResponse {
  message: string;
}

// ============================================================================
// Validation Schemas
// ============================================================================

// Update schemas for consistency
export const UpdateUserProfileSchema = z.object({
  firstName: z.string().min(1).max(50).optional(),
  middleName: z.string().max(50).optional(),
  lastName: z.string().min(1).max(50).optional(),
  /*
    `phoneNumber`, matching `UpdateProfileDto`. It read `phone` until this was
    corrected — a key the backend does not accept, and one the global
    `ValidationPipe` (`forbidNonWhitelisted: true`) would 400 rather than
    ignore. Nothing sent it, so nothing broke; the schema was simply describing
    a field that did not exist.
  */
  phoneNumber: z.string().max(30).optional(),
  title: z.string().max(200).optional(),
  emailSignature: z.string().max(1000).optional(),
  // Personal HTML email signature — matches the backend 200KB cap (UpdateProfileDto).
  // Empty string is allowed (clears the signature), so no min length.
  htmlEmailSignature: z.string().max(200000).optional(),
  htmlLetterFooter: z.string().max(200000).optional()
});

// Password changes are handled entirely by Keycloak via the in-app
// Application-Initiated Action (`kc_action=UPDATE_PASSWORD`) — see
// `ChangePasswordSection`. There is no app-side password DTO/schema: the app never
// receives the current or new password.

export const SubmitSuggestionSchema = z.object({
  suggestion: z
    .string()
    .min(1, 'Suggestion is required')
    .max(2000, 'Suggestion must be 2000 characters or less'),
  fileIds: z
    .array(z.number().int().positive())
    .max(5, 'Maximum 5 files allowed')
    .default([])
});

// ============================================================================
// 2FA Types
// ============================================================================

/**
 * 2FA Enable Response
 */
export interface Enable2FAResponse {
  success: boolean;
  message: string;
  action: 'logout_required' | 'none';
}

/**
 * 2FA Disable Response
 */
export interface Disable2FAResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// Utility Types
// ============================================================================

export type ProfileUpdateResult<T> =
  | {
      success: true;
      data: T;
      message?: string;
    }
  | {
      success: false;
      error: string;
      validationErrors?: Record<string, string[]>;
    };

// Alias for consistency with other services
export type ProfileActionResult<T> = ProfileUpdateResult<T>;

export interface ProfileCacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// ============================================================================
// Event Types for Profile Updates
// ============================================================================

export interface ProfileUpdateEvent {
  type: 'user_profile_updated' | 'firm_profile_updated';
  userId?: string;
  firmId?: string;
  changes:
    | UpdateUserProfileRequest
    | UpdateLawFirmProfileRequest
    | Record<string, unknown>;
  timestamp: string;
}
